// The one way ResAI turns a stored instant into text a person reads.
//
// Three forms and nothing else: `short` where a timestamp is scanned (lists, cards,
// table cells), `long` where it is read (pages, panels, chat), `static` for anything
// that leaves the product (exports, prints, emails), which never goes relative because
// the moment it is read is not the moment it was made. The ladder they follow is
// defined on the UX standard "Date and Time Formats"; vectors.json beside this file is
// its executable form, and every runtime that formats timestamps -- the three
// frontends here and the backend -- must pass it.
//
// There is deliberately no pattern, locale or option argument. A feature that needs a
// shape the three forms do not produce changes the standard, not this call. The one
// other shape the standard defines, a span of calendar days, is formatDateRange.
//
// The display zone and the clock are not the caller's either: an app sets them once,
// from the signed-in profile, with configureTimestamps(), and the zone is resolved here.
//
// This file is copied into each frontend by scripts/sync-datetime.sh. Edit it here;
// the copies are checked for drift.

const FORMS = new Set(['short', 'long', 'static']);

// What every form returns for a missing or unreadable instant. Callers that want
// their own empty-state copy ("Not run yet") compare against this; none may
// substitute a date.
export const ABSENT = '—';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Zone abbreviations, keyed by the zone's UTC offset in minutes at the instant.
//
// Pinned here rather than asked of the platform because the platforms disagree:
// browsers give `GMT-4` for Halifax under some locales and never give `AEDT`, while
// the backend's zone database gives `AEDT` and `+06`. A table both runtimes share
// cannot drift. A zone or offset missing from it gets the numeric offset, which is
// always correct if never pretty: `UTC+06:00`.
export const ZONE_ABBREVIATIONS = {
  UTC: { 0: 'UTC' },
  'Etc/UTC': { 0: 'UTC' },
  'America/St_Johns': { [-210]: 'NST', [-150]: 'NDT' },
  'America/Halifax': { [-240]: 'AST', [-180]: 'ADT' },
  'America/Moncton': { [-240]: 'AST', [-180]: 'ADT' },
  'America/Glace_Bay': { [-240]: 'AST', [-180]: 'ADT' },
  'America/Toronto': { [-300]: 'EST', [-240]: 'EDT' },
  'America/New_York': { [-300]: 'EST', [-240]: 'EDT' },
  'America/Winnipeg': { [-360]: 'CST', [-300]: 'CDT' },
  'America/Chicago': { [-360]: 'CST', [-300]: 'CDT' },
  'America/Regina': { [-360]: 'CST' },
  'America/Edmonton': { [-420]: 'MST', [-360]: 'MDT' },
  'America/Denver': { [-420]: 'MST', [-360]: 'MDT' },
  'America/Phoenix': { [-420]: 'MST' },
  'America/Whitehorse': { [-420]: 'MST' },
  'America/Vancouver': { [-480]: 'PST', [-420]: 'PDT' },
  'America/Los_Angeles': { [-480]: 'PST', [-420]: 'PDT' },
  'America/Anchorage': { [-540]: 'AKST', [-480]: 'AKDT' },
  'Pacific/Honolulu': { [-600]: 'HST' },
  'Europe/London': { 0: 'GMT', 60: 'BST' },
  'Europe/Paris': { 60: 'CET', 120: 'CEST' },
};

let settings = { zone: null, orgZone: null, clock: '12h' };

// Called by each app once the signed-in profile is known, and again if it changes,
// with the organisation's Primary Time Zone, clock and time display mode, and the
// user's own zone. The device's zone is read here; a caller passes one only to test.
export function configureTimestamps({ orgZone, clock, mode, userZone, deviceZone = deviceTimeZone() } = {}) {
  settings = {
    zone: displayZone({ mode, orgZone, userZone, deviceZone }),
    orgZone: readableZone(orgZone),
    clock: clock === '24h' ? '24h' : '12h',
  };
}

// The settings configureTimestamps resolved: the zone this person reads times in, the
// organisation's own zone, and the clock. For screens that ask about a zone rather than
// show a time, such as a schedule's zone picker defaulting to the reader's. A zone is
// null when there is none, which reads as UTC.
export function timeSettings() {
  return { ...settings };
}

// A schedule's time of day in its own zone, with that zone's abbreviation as it stands
// today and the organisation's clock: "8:00 AM ADT", "20:30 NST", "9:00 AM UTC+05:30".
// Not one of the three forms: a recurring time is not an instant, so it never goes
// relative and carries no date. `hhmm` is "HH:MM"; anything else is absent.
export function formatScheduleTime(hhmm, zone) {
  const m = typeof hhmm === 'string' ? /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim()) : null;
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return ABSENT;
  const at = { hour: Number(m[1]), minute: Number(m[2]) };
  const name = readableZone(zone) || 'UTC';
  const nowMs = Date.now();
  return `${clockText(at, settings.clock)} ${zoneSuffix(nowMs, name, wallClock(nowMs, name))}`;
}

// The zone a person reads times in. In 'everyone' mode, the default, it is the
// organisation's Primary Time Zone, so two people anywhere read the same clock for the
// same moment. In 'users' mode it is the zone in their profile, else their device's,
// else the organisation's. A zone this runtime cannot load is skipped, and with none
// left rendering falls back to UTC rather than throwing. Report periods never follow
// this: they are calendar days in the organisation's zone, whatever anyone reads.
export function displayZone({ mode, orgZone, userZone, deviceZone } = {}) {
  if (mode === 'users') return readableZone(userZone) || readableZone(deviceZone) || readableZone(orgZone);
  return readableZone(orgZone);
}

// The zone this device is set to, or null when the runtime will not say.
export function deviceTimeZone() {
  try {
    return readableZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

export function formatTimestamp(instant, form) {
  if (!FORMS.has(form)) return ABSENT;
  const ms = toMillis(instant);
  if (ms === null) return ABSENT;
  const zone = settings.zone || 'UTC';
  const at = wallClock(ms, zone);
  if (form === 'static') return `${fullDate(at)}, ${clockText(at, settings.clock)} ${zoneSuffix(ms, zone, at)}`;

  const nowMs = Date.now();
  const now = wallClock(nowMs, zone);
  // Calendar days in the display zone, not elapsed time: at 02:00, 21:00 the
  // evening before is Yesterday even though only five hours have passed.
  const days = dayNumber(now) - dayNumber(at);
  const elapsed = nowMs - ms;
  return form === 'short' ? shortForm(at, now, days, elapsed) : `${longDay(at, days)}, ${clockText(at, settings.clock)}`;
}

function shortForm(at, now, days, elapsed) {
  if (elapsed >= 0) {
    if (elapsed < MINUTE) return 'Just now';
    if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`;
    // Hours, not "Yesterday", while under a day has passed: the standard's own
    // example reads 21:00 the evening before as hours ago at 02:00.
    if (days === 0 || elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`;
    if (days === 1) return 'Yesterday';
  } else {
    const ahead = -elapsed;
    if (ahead < MINUTE) return 'Now';
    if (ahead < HOUR) return `In ${Math.floor(ahead / MINUTE)}m`;
    // Future hours stop at midnight: at 22:00 a 04:00 run is Tomorrow, not In 6h.
    if (days === 0) return `In ${Math.floor(ahead / HOUR)}h`;
    if (days === -1) return 'Tomorrow';
  }
  if (Math.abs(days) <= 6) return WEEKDAYS[at.weekday];
  // The year only when it is not this one. A recent prior-year date stays on the
  // weekday rung above.
  const short = `${at.day} ${MONTHS[at.month - 1].slice(0, 3)}`;
  return at.year === now.year ? short : `${short} ${at.year}`;
}

function longDay(at, days) {
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days === -1) return 'Tomorrow';
  if (Math.abs(days) <= 6) return WEEKDAYS[at.weekday];
  return fullDate(at);
}

function fullDate(at) {
  return `${at.day} ${MONTHS[at.month - 1]} ${at.year}`;
}

function clockText(at, clock) {
  const minutes = String(at.minute).padStart(2, '0');
  if (clock === '24h') return `${String(at.hour).padStart(2, '0')}:${minutes}`;
  return `${at.hour % 12 || 12}:${minutes} ${at.hour < 12 ? 'AM' : 'PM'}`;
}

function zoneSuffix(ms, zone, at) {
  const offset = Math.round((utc(at.year, at.month - 1, at.day, at.hour, at.minute).getTime() - floorToMinute(ms)) / MINUTE);
  const named = ZONE_ABBREVIATIONS[zone] && ZONE_ABBREVIATIONS[zone][offset];
  if (named) return named;
  const sign = offset < 0 ? '-' : '+';
  const abs = Math.abs(offset);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

// ---- reading the instant --------------------------------------------------------

// A string must carry its offset. "2026-10-07T09:42:00" names a moment only once a
// zone is assumed, and guessing one is how a time shifts by hours unnoticed.
//
// Parsed here rather than by Date.parse, which only promises three fractional digits
// and a "+HH:MM" offset: anything else -- Python's six digits, Postgres's bare "+00"
// -- is each engine's leniency, so the same instant could read in one browser and be
// absent in another.
const ISO_WITH_OFFSET =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|([+-])(\d{2})(?::?(\d{2}))?)$/i;

// A span of calendar days, both ends included, as the standard words a range: "1 Sep to
// 30 Sep 2026", with the year on both ends only when they differ ("15 Dec 2025 to 14 Jan
// 2026"). The ends are calendar dates ("2026-09-01"), not instants: a report period is
// whole days in a zone the caller has already decided, so nothing here may move a day.
// Anything else, or an end before the start, is absent.
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDateRange(from, to) {
  const a = calendarDate(from);
  const b = calendarDate(to);
  if (!a || !b || utc(a.year, a.month - 1, a.day) > utc(b.year, b.month - 1, b.day)) return ABSENT;
  const day = (d) => `${d.day} ${MONTHS[d.month - 1].slice(0, 3)}`;
  return a.year === b.year ? `${day(a)} to ${day(b)} ${b.year}` : `${day(a)} ${a.year} to ${day(b)} ${b.year}`;
}

function calendarDate(text) {
  const m = typeof text === 'string' ? CALENDAR_DATE.exec(text.trim()) : null;
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const check = utc(year, month - 1, day);
  // A date the calendar does not have (31 September) rolls over in Date; refuse it instead.
  if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  return { year, month, day };
}

// The instant as ISO 8601 in UTC, read the same way formatTimestamp reads it, or null
// when it is missing or unreadable. For machine-readable markup such as
// <time datetime>; it is not a display form.
export function toIsoInstant(instant) {
  const ms = toMillis(instant);
  return ms === null ? null : new Date(ms).toISOString();
}

// A Date, or ISO 8601 text with an offset. A bare number is refused: nothing says
// whether it counts seconds or milliseconds, and guessing wrong is a confident 1970.
function toMillis(instant) {
  if (instant instanceof Date) {
    const ms = instant.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof instant !== 'string') return null;
  const m = ISO_WITH_OFFSET.exec(instant.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, sec = '0', fraction = '', zone, sign, offH, offM = '0'] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const hour = Number(h);
  const minute = Number(mi);
  const second = Number(sec);
  if (month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) return null;
  if (day < 1 || day > utc(year, month, 0).getUTCDate()) return null;
  const millis = Number(`${fraction}000`.slice(0, 3));
  const offset = zone.toUpperCase() === 'Z' ? 0 : (sign === '-' ? -1 : 1) * (Number(offH) * 60 + Number(offM));
  if (Math.abs(offset) > 18 * 60) return null;
  return utc(year, month - 1, day, hour, minute, second, millis).getTime() - offset * MINUTE;
}

// Date.UTC reads years 0-99 as 1900-1999; setting the year afterwards does not.
function utc(year, ...rest) {
  const date = new Date(Date.UTC(2000, ...rest));
  date.setUTCFullYear(year);
  return date;
}

// ---- the display zone's wall clock ----------------------------------------------

const partFormatters = new Map();

function readableZone(zone) {
  if (!zone || typeof zone !== 'string') return null;
  try {
    partsIn(zone);
    return zone;
  } catch {
    return null;
  }
}

// Numeric parts only, so nothing here depends on the browser's language. The words
// (month and weekday names) come from the fixed lists above.
function partsIn(zone) {
  let formatter = partFormatters.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    });
    partFormatters.set(zone, formatter);
  }
  return formatter;
}

function wallClock(ms, zone) {
  const parts = {};
  for (const part of partsIn(zone).formatToParts(new Date(ms))) parts[part.type] = part.value;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  return {
    year,
    month,
    day,
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: utc(year, month - 1, day).getUTCDay(),
  };
}

function dayNumber(at) {
  return utc(at.year, at.month - 1, at.day).getTime() / DAY;
}

function floorToMinute(ms) {
  return Math.floor(ms / MINUTE) * MINUTE;
}
